/**
 * Every evaluation, newest first and grouped by day, each beside the market value now,
 * so OHC can see how their calls turned out (the brief, point 4). Opening one shows it
 * in full.
 */

import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, History as HistoryIcon } from "lucide-react";
import { type Evaluation, type EvaluationRow, evaluateApi } from "./api";
import { EvaluationView } from "./EvaluationView";
import { cardNumber, dayHeading, LANGUAGE_LABEL, sgDate, sgdCents } from "./format";
import { Change, Chip, EmptyState, Skeleton, VerdictPill } from "./ui";

export function History({ onOpen, onError }: { onOpen: (id: number) => void; onError: (e: unknown) => void }) {
  const [rows, setRows] = useState<EvaluationRow[] | null>(null);
  useEffect(() => {
    evaluateApi.list().then((r) => setRows(r.evaluations)).catch((e) => {
      onError(e);
      setRows([]);
    });
  }, [onError]);

  const groups: Array<{ date: string; rows: EvaluationRow[] }> = [];
  for (const row of rows ?? []) {
    const date = sgDate(row.createdAt) ?? "";
    const last = groups[groups.length - 1];
    if (last && last.date === date) last.rows.push(row);
    else groups.push({ date, rows: [row] });
  }

  return (
    <div className="px-4 pb-6 pt-4">
      <div className="mb-4">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900">History</h1>
        <p className="text-sm text-slate-500">Every evaluation, with the market value then and now.</p>
      </div>

      {!rows && (
        <div className="card divide-y divide-slate-100">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3.5">
              <Skeleton className="h-6 w-14" />
              <div className="flex-1 space-y-2"><Skeleton className="h-4 w-1/2" /><Skeleton className="h-3 w-2/3" /></div>
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      )}

      {rows && rows.length === 0 && (
        <div className="card">
          <EmptyState icon={<HistoryIcon className="h-6 w-6" />} title="No evaluations yet">
            Open a card, enter a price and press Evaluate. Each one is kept here.
          </EmptyState>
        </div>
      )}

      <div className="space-y-5">
        {groups.map((g) => (
          <section key={g.date}>
            <div className="label mb-2 px-1">{dayHeading(g.date)}</div>
            <ul className="card divide-y divide-slate-100 overflow-hidden">
              {g.rows.map((r) => (
                <li key={r.id}>
                  <button type="button" onClick={() => onOpen(r.id)} className="group flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-slate-50">
                    <VerdictPill verdict={r.verdict} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-semibold text-slate-900">{r.card.cardName}</div>
                      <div className="mt-1 flex items-center gap-1.5">
                        <Chip tone={r.grade === "psa10" ? "gold" : "slate"}>{r.grade === "psa10" ? "PSA 10" : `Raw ${r.condition ?? ""}`}</Chip>
                        <Chip tone="navy">{r.place === "online" ? "Online" : "Show"}</Chip>
                        <span className="num whitespace-nowrap text-xs text-slate-500">{LANGUAGE_LABEL[r.card.language]} {cardNumber(r.card.number, r.card.setTotal)}</span>
                      </div>
                      <div className="mt-1 truncate text-[11px] text-slate-400">
                        {r.card.setName} · {r.by}{r.note ? ` · ${r.note}` : ""}
                      </div>
                    </div>
                    <div className="flex-none text-right">
                      <div className="num text-[15px] font-semibold text-slate-900">{sgdCents(r.priceSgdCents)}</div>
                      <div className="num text-[11px] text-slate-500">value {sgdCents(r.valueNowSgdCents ?? r.valueThenSgdCents)}</div>
                      {r.changePercent != null && Math.abs(r.changePercent) >= 0.05 && <Change percent={r.changePercent} className="text-[11px]" />}
                    </div>
                    <ChevronRight className="hidden h-4 w-4 flex-none text-slate-300 sm:block" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      {rows && rows.length > 0 && (
        <p className="mt-4 px-1 text-xs text-slate-400">
          The value shown is the market value now and its change since the evaluation, from the sales already read, with today's settings and rate. Open the card to read the newest sales.
        </p>
      )}
    </div>
  );
}

export function HistoryEntry({ id, onBack, onCard, onError }: { id: number; onBack: () => void; onCard: (id: number) => void; onError: (e: unknown) => void }) {
  const [e, setE] = useState<Evaluation | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    evaluateApi.get(id).then((r) => setE(r.evaluation)).catch((err) => {
      onError(err);
      setFailed(true);
    });
  }, [id, onError]);
  return (
    <div className="space-y-3 px-4 pb-6 pt-3">
      <div className="flex items-center justify-between text-sm font-medium">
        <button type="button" onClick={onBack} className="-ml-1 flex items-center gap-0.5 text-navy-700 hover:text-navy-900">
          <ChevronLeft className="h-4 w-4" />
          History
        </button>
        {e && (
          <button type="button" onClick={() => onCard(e.card.id)} className="flex items-center gap-0.5 text-navy-700 hover:text-navy-900">
            Open the card
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
      {failed && <div className="card p-5 text-sm text-rose-700">This evaluation could not be opened.</div>}
      {!failed && !e && <div className="card space-y-3 p-4"><Skeleton className="h-14 w-full" /><Skeleton className="h-24 w-full" /><Skeleton className="h-40 w-full" /></div>}
      {e && <div className="animate-fadein"><EvaluationView e={e} showCard /></div>}
    </div>
  );
}
